// Quick guide — Stress & written accents (A1). The two default rules, when you
// write the accent, and the special cases (esdrújulas, diacritics, question
// words, hiatus). Hand-drawn paper style with audio on examples.
import { speakButtonHTML } from "../lib/audio.js";

function ex(words) {
  return `<div class="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">${words
    .map((w) => `<span class="text-sage-800">${w}${speakButtonHTML(w.replace(/\s*\(.*/, ""))}</span>`)
    .join("")}</div>`;
}

export function stressAccentsView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Stress & Accents ´</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Spanish stress is rule-based. Learn the two defaults, and the written accent (´) just marks the exceptions.</p>
      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg space-y-5">

        <div>
          <h3 class="text-2xl text-emerald-700 sketch-underline inline-block">1 · The two default rules</h3>
          <p class="text-sage-700 mt-1">No accent needed when the stress follows these:</p>
          <p class="text-sage-700 mt-2">Ends in a <b>vowel, -n or -s</b> → stress the <b>second-to-last</b> syllable.</p>
          ${ex(["casa", "hablan", "lunes", "problema"])}
          <p class="text-sage-700 mt-2">Ends in <b>any other consonant</b> → stress the <b>last</b> syllable.</p>
          ${ex(["hablar", "ciudad", "feliz", "reloj"])}
        </div>

        <div>
          <h3 class="text-2xl text-amber-700 sketch-underline inline-block">2 · The accent marks the exception</h3>
          <p class="text-sage-700 mt-1">When the stress lands where the rules <i>don't</i> predict, you write the accent.</p>
          ${ex(["corazón", "árbol", "lápiz", "café", "fácil", "sofá"])}
        </div>

        <div>
          <h3 class="text-2xl text-sky-700 sketch-underline inline-block">3 · Esdrújulas — always an accent</h3>
          <p class="text-sage-700 mt-1">Stress on the <b>third-from-last</b> syllable <b>always</b> takes an accent.</p>
          ${ex(["música", "teléfono", "rápido", "pájaro", "médico"])}
        </div>

        <div>
          <h3 class="text-2xl text-rose-600 sketch-underline inline-block">4 · Accents that change meaning</h3>
          <p class="text-sage-700 mt-1">Same spelling, accent flips the meaning (diacritics):</p>
          <div class="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-base">
            <span><b>tú</b> you · <b>tu</b> your</span>
            <span><b>él</b> he · <b>el</b> the</span>
            <span><b>sí</b> yes · <b>si</b> if</span>
            <span><b>más</b> more · <b>mas</b> but</span>
            <span><b>sé</b> I know · <b>se</b> (reflexive)</span>
            <span><b>té</b> tea · <b>te</b> you</span>
            <span><b>mí</b> me · <b>mi</b> my</span>
          </div>
        </div>

        <div>
          <h3 class="text-2xl text-violet-700 sketch-underline inline-block">5 · Question words</h3>
          <p class="text-sage-700 mt-1">Always carry an accent — in questions <i>and</i> exclamations.</p>
          ${ex(["qué", "quién", "cómo", "cuándo", "dónde", "cuánto", "por qué"])}
        </div>

        <div>
          <h3 class="text-2xl text-teal-700 sketch-underline inline-block">6 · Breaking a diphthong</h3>
          <p class="text-sage-700 mt-1">An accent on a weak vowel (<b>í</b>, <b>ú</b>) splits it into its own syllable.</p>
          ${ex(["día", "país", "río", "baúl", "María"])}
        </div>

      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Rule of thumb: if a word “sounds wrong” read by the default rules, it probably needs an accent.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
